


import Chart from "chart.js/auto";
import { Component, Vue } from "vue-property-decorator";

import { AgentKb } from "App";
import { EventBus } from "App/IOC";
import { Column, PagerSorter } from "App/Models";
import {
    AgentKbExchange, AgentKbFilters, AgentKbUsage
} from "App/Models/AgentKb";

import Table from "Web/Components/Table/Index.vue";

import Thread, { HelpScoutUrl } from "./Thread.vue";
import { CompanyOf, LoadCompanies } from "./Companies";

/* L'AGENT IA — ce que les utilisateurs demandent a l'agent de la base de
   connaissance, et ce qu'ils pensent de ses reponses ( Fred, 23/09/2026, sur le
   modele des rapports HelpScout : Happiness, Docs et Email Report ).

   Tout est LU chez le service de l'agent, par le relais `Internal/AgentKb/*` du
   back : la Console ne range rien. La cle du service reste cote serveur.

   ⚠️ `to` EST EXCLUSIF cote service ( mesure du 23/09/2026 : du 22 au 22 rend 0
   alors que le 22 compte trois echanges ). On envoie donc le LENDEMAIN du dernier
   jour de la periode. */

type Period = 7 | 30 | 90 | 365;

type RatingFilter = "all" | "up" | "down" | "none" | "sent" | "failed";

interface Totals {
    all: number;
    up: number;
    down: number;
    none: number;
    sent: number;
    failed: number;
    conversations: number;
}

interface ExchangeRow extends AgentKbExchange {
    Id: number;
    Date: string;
    User: string;
    Group: string;
    Question: string;
    Article: string;
    Rating: string;
    Reason: string;
    Comment: string;
}

interface ArticleCount {
    Article: string;
    Title: string | null;
    Url: string | null;
    Count: number;
}

/* Au-dela, le service refuse : c'est aussi la taille de l'echantillon sur lequel
   on compte les articles servis et rejetes. */
const SAMPLE_LIMIT = 200;

/* Toutes les minutes. */
const AUTO_REFRESH = 60000;

function isoDay( date: Date ): string {
    const month = String( date.getMonth() + 1 ).padStart( 2, "0" );
    const day = String( date.getDate() ).padStart( 2, "0" );

    return `${ date.getFullYear() }-${ month }-${ day }`;
}

@Component( { components: { Table, Thread } } )
export default class AgentKbView extends Vue {
    private readonly periods: Period[] = [ 7, 30, 90, 365 ];
    private readonly ratingFilters: RatingFilter[] = [
        "all", "up", "down", "none", "sent", "failed"
    ];

    private period: Period = 30;
    private groupId: string = "";
    private groupTimer: number = 0;

    /* LA MISE A JOUR AUTOMATIQUE, toutes les minutes ( Fred, 26/09/2026 ). */
    private autoTimer: number = 0;
    private refreshing: boolean = false;
    private updatedAt: string = "";

    /* UN UTILISATEUR, et une recherche dans les questions ( Fred, 26/09/2026 :
       « voir et filtrer toutes les questions posees par un user » ). Le service
       les accepte deja ( `userEmail`, `q` ) et le relais les laisse passer.
       ⚠️ `userEmail` veut l'adresse EXACTE : un morceau d'adresse rend 0. */
    private userEmail: string = "";
    private search: string = "";

    /* UN ARTICLE ( Fred, 26/09/2026 ) : un clic sur un article servi ou rejete
       montre toutes les questions auxquelles il a servi — c'est la qu'on voit
       ce qu'il faut corriger dans l'article. */
    private article: ArticleCount | null = null;

    /* Le volet montre l'echange seul, ou tout son fil. */
    private showThread: boolean = false;

    private loading: boolean = false;
    private totals: Totals = {
        all: 0, up: 0, down: 0, none: 0, sent: 0, failed: 0, conversations: 0
    };
    private usage: AgentKbUsage | null = null;
    private served: ArticleCount[] = [];
    private rejected: ArticleCount[] = [];
    private sampled: boolean = false;

    private rating: RatingFilter = "all";
    private rows: ExchangeRow[] = [];
    private rowsLoading: boolean = false;
    private columns: Column[] = [];
    private pagerSorter: PagerSorter = new PagerSorter( 25 );

    /* L'echange ouvert dans le volet de droite. `null` = volet ferme. */
    private selected: ExchangeRow | null = null;

    private chart: Chart | null = null;

    /* Faux des que la vue est quittee : une reponse qui arrive ensuite ne doit
       plus toucher a la vue ( `$t` y leve une erreur, `$i18n` etant deja nul ). */
    private alive: boolean = true;

    /* PENDANT LE CHARGEMENT, « … » a la place des chiffres ( Fred, 26/09/2026 ) :
       un 0 affiche le temps que la liste arrive se lit comme « personne n'a rien
       demande ». */
    private shown( value: number | string ): string {
        return this.loading ? "…" : String( value );
    }

    private countOf( rating: RatingFilter ): string {
        return this.shown( this.ratingFilterCount( rating ) );
    }

    private get votes(): number {
        return this.totals.up + this.totals.down;
    }

    private get upRate(): number {
        return this.votes === 0 ? 0 : Math.round( this.totals.up * 100 / this.votes );
    }

    private get downRate(): number {
        return this.votes === 0 ? 0 : 100 - this.upRate;
    }

    /* La part des reponses qui ont recu un vote — l'equivalent du « based on 43
       ratings from 27 % of customers » de HelpScout. */
    private get voteRate(): number {
        return this.totals.all === 0 ? 0 : Math.round( this.votes * 100 / this.totals.all );
    }

    private get averageDuration(): string {
        if( this.usage === null || this.usage.total.echanges === 0 )
            return "—";

        const seconds = this.usage.total.durationMsTotal / this.usage.total.echanges / 1000;

        return seconds.toLocaleString( "fr-FR", { maximumFractionDigits: 1 } ) + " s";
    }

    private get tokens(): string {
        if( this.usage === null )
            return "—";

        const total = this.usage.total.inputTokens + this.usage.total.outputTokens;

        return total.toLocaleString( "fr-FR" );
    }

    /* LE CACHE, a part : le sommaire des articles est relu en cache a chaque
       question, et pese bien plus lourd que l'entree et la sortie reunies
       ( 1,68 M lus contre 333 k en entree, mesure du 26/09/2026 ). */
    private get cacheTokens(): string {
        if( this.usage === null )
            return "";

        const cached = this.usage.total.cacheReadTokens + this.usage.total.cacheWriteTokens;

        return cached.toLocaleString( "fr-FR" );
    }

    private created(): void {
        this.columns = [
            /* Cinq colonnes, sur le modele de la liste Ratings de HelpScout ( Fred,
               23/09/2026 ). L'avis n'est que le POUCE : les commentaires des contents
               et des mecontents se lisent dans la meme colonne. Le groupe passe sous
               l'adresse ; la raison et l'article vivent dans le volet de detail. */
            Column.Static( "Date", this.$t( "AGENTKB_columnDate" ) as string ),
            Column.Static( "User", this.$t( "AGENTKB_columnUser" ) as string ),
            Column.Static( "Question", this.$t( "AGENTKB_columnQuestion" ) as string ),
            Column.Static( "Rating", this.$t( "AGENTKB_columnRating" ) as string ),
            Column.Static( "Comment", this.$t( "AGENTKB_columnComment" ) as string )
        ];

        /* Venue de « Par utilisateur » ou d'« Usage » : `?user=` et `?group=`
           posent le filtre. */
        const user = this.$route.query.user;
        const group = this.$route.query.group;

        if( typeof user === "string" )
            this.userEmail = user;

        if( typeof group === "string" )
            this.groupId = group;

        this.refresh();

        this.autoTimer = window.setInterval( () => this.autoRefresh(), AUTO_REFRESH );
    }

    private beforeDestroy(): void {
        this.alive = false;
        window.clearTimeout( this.groupTimer );
        window.clearInterval( this.autoTimer );
        this.destroyChart();
    }

    /* SILENCIEUSE : ni « … », ni points sur le tableau, la page et le volet
       ouvert restent ou ils sont — on lit, les chiffres se mettent a jour
       dessous. Rien quand l'onglet est en arriere-plan ( une dizaine d'appels
       par minute pour un ecran que personne ne regarde ), ni quand un
       chargement est deja en cours. */
    private autoRefresh(): void {
        if( document.hidden || this.refreshing )
            return;

        this.refresh( true );
    }

    private selectPeriod( period: Period ): void {
        this.period = period;
        this.refresh();
    }

    /* Un clic sur un utilisateur, dans le tableau ou le volet : toutes ses
       questions, sur la periode. */
    private filterUser( email: string ): void {
        this.userEmail = email;
        this.selected = null;
        this.refresh();
    }

    private filterArticle( entry: ArticleCount ): void {
        this.article = entry;
        this.selected = null;
        this.refresh();
    }

    private clearArticle(): void {
        this.article = null;
        this.refresh();
    }

    /* « Groupe 204 · Immoove » — la societe quand la Console la connait. */
    private groupLabel( group: number ): string {
        const company = CompanyOf( group );

        return this.$t( "AGENTKB_columnGroup" ) + " " + String( group )
            + ( company !== "" ? " · " + company : "" );
    }

    private helpScoutOf( item: AgentKbExchange ): string {
        return item.helpscoutConversationId !== null
            ? HelpScoutUrl( item.helpscoutConversationId )
            : "";
    }

    private clearUser(): void {
        this.userEmail = "";
        this.refresh();
    }

    private onGroupInput(): void {
        window.clearTimeout( this.groupTimer );

        this.groupTimer = window.setTimeout( () => this.refresh(), 400 );
    }

    private selectRating( rating: RatingFilter ): void {
        this.rating = rating;
        this.pagerSorter.Page = 1;
        this.fetchRows();
    }

    private onPager( pagerSorter: PagerSorter ): void {
        this.pagerSorter = pagerSorter;
        this.fetchRows();
    }

    private openRow( action: string, rows: ExchangeRow[] ): void {
        if( action === "open" && rows.length > 0 ) {
            this.selected = rows[ 0 ];
            this.showThread = false;
        }
    }

    private closeDetail(): void {
        this.selected = null;
    }

    /* Les filtres communs a tous les appels : la periode et le groupe. */
    private baseFilters(): AgentKbFilters {
        const from = new Date();
        const to = new Date();

        from.setDate( from.getDate() - this.period + 1 );
        to.setDate( to.getDate() + 1 );

        /* `q` ne filtre pas la courbe : `usage` ne l'accepte pas, le relais le
           retire. Les compteurs, les articles et le tableau le suivent. */
        return {
            from: isoDay( from ),
            to: isoDay( to ),
            groupId: this.groupId.trim(),
            userEmail: this.userEmail.trim().toLowerCase(),
            q: this.search.trim(),
            articleKbId: this.article !== null ? this.article.Article : ""
        };
    }

    private ratingFiltersOf( rating: RatingFilter ): AgentKbFilters {
        switch( rating ) {
            case "up":
                return { score: "1" };

            case "down":
                return { score: "-1" };

            case "none":
                return { score: "none" };

            case "sent":
                return { support: "sent" };

            case "failed":
                return { support: "failed" };

            default:
                return {};
        }
    }

    /* ⚠️ UN APPEL APRES L'AUTRE, ET LE TABLEAU D'ABORD ( Fred, 26/09/2026 : « met
       beaucoup de temps » ). Le service de l'agent tourne sur Lambda : une
       instance deja eveillee repond en 0,1 s, une instance a eveiller en 3,5 a
       7 s. Lancer la dizaine d'appels EN PARALLELE en eveillait une par appel —
       4,7 s mesurees pour dix appels, puis le tableau APRES. En file, ils
       passent tous par la meme instance : au pire un reveil, puis ~0,1 s
       chacun. Le tableau part en premier, c'est lui qu'on vient lire. */
    private async refresh( silent: boolean = false ): Promise<void> {
        this.refreshing = true;

        if( !silent ) {
            this.loading = true;
            this.pagerSorter.Page = 1;
        }

        await this.fetchRows( silent );

        if( !this.alive )
            return;

        await this.fetchTotals();
        await this.fetchUsage();
        await this.fetchArticles();

        if( !this.alive )
            return;

        this.loading = false;
        this.refreshing = false;
        this.updatedAt = new Date().toLocaleTimeString( "fr-FR", {
            hour: "2-digit", minute: "2-digit"
        } );

        await this.$nextTick();

        if( this.alive )
            this.buildChart();
    }

    /* Chaque compteur est le `total` d'une liste filtree, demandee a une ligne :
       le service ne rend pas d'agregat de votes. */
    private async fetchTotals(): Promise<void> {
        const base = this.baseFilters();
        const count = async ( rating: RatingFilter ): Promise<number> => {
            const [ page ] = await AgentKb.Exchanges( {
                ...base, ...this.ratingFiltersOf( rating ), limit: 1
            } );

            return page !== undefined ? page.total : 0;
        };

        /* En file, pas en parallele — voir `refresh`. */
        const counts: number[] = [];

        for( const rating of this.ratingFilters )
            counts.push( await count( rating ) );

        const [ all, up, down, none, sent, failed ] = counts;

        const [ conversations ] = await AgentKb.Conversations( { ...base, limit: 1 } );

        this.totals = {
            all, up, down, none, sent, failed,
            conversations: conversations !== undefined ? conversations.total : 0
        };
    }

    private async fetchUsage(): Promise<void> {
        const [ usage, error ] = await AgentKb.Usage( "day", this.baseFilters() );

        if( error !== undefined ) {
            EventBus.Publish( "Error", error );
            return;
        }

        this.usage = usage !== undefined ? usage : null;
    }

    /* Les articles les plus servis et les plus rejetes, comptes sur les 200
       derniers echanges de la periode : le service ne les agrege pas. */
    private async fetchArticles(): Promise<void> {
        const base = this.baseFilters();
        const [ all ] = await AgentKb.Exchanges( { ...base, limit: SAMPLE_LIMIT } );
        const [ down ] = await AgentKb.Exchanges( { ...base, score: "-1", limit: SAMPLE_LIMIT } );

        this.served = all !== undefined ? this.countArticles( all.items ) : [];
        this.rejected = down !== undefined ? this.countArticles( down.items ) : [];
        this.sampled = all !== undefined && all.total > SAMPLE_LIMIT;
    }

    private countArticles( items: AgentKbExchange[] ): ArticleCount[] {
        const counts: { [ article: string ]: ArticleCount } = {};

        items.forEach( ( item: AgentKbExchange ): void => {
            if( item.articleKbId === null || item.articleKbId === "" )
                return;

            const entry = counts[ item.articleKbId ] || {
                Article: item.articleKbId,
                Title: null,
                Url: null,
                Count: 0
            };

            entry.Count++;
            // Le titre et l'adresse d'un meme article ne varient pas d'un echange
            // a l'autre : on garde le premier rendu.
            entry.Title = entry.Title || item.articleTitle || null;
            entry.Url = entry.Url || item.articleUrl || null;
            counts[ item.articleKbId ] = entry;
        } );

        return Object.keys( counts )
            .map( article => counts[ article ] )
            .sort( ( a, b ) => b.Count - a.Count )
            .slice( 0, 5 );
    }

    private async fetchRows( silent: boolean = false ): Promise<void> {
        this.rowsLoading = !silent;

        const [ page, error ] = await AgentKb.Exchanges( {
            ...this.baseFilters(),
            ...this.ratingFiltersOf( this.rating ),
            page: this.pagerSorter.Page,
            limit: this.pagerSorter.Limit
        } );

        if( !this.alive )
            return;

        this.rowsLoading = false;

        if( error !== undefined || page === undefined ) {
            if( error !== undefined )
                EventBus.Publish( "Error", error );

            return;
        }

        this.rows = page.items.map( item => this.toRow( item ) );
        this.pagerSorter.Total = page.total;

        void LoadCompanies( page.items.map( item => item.groupId ) );
    }

    private toRow( item: AgentKbExchange ): ExchangeRow {
        return {
            ...item,
            Id: item.id,
            Date: this.formatDate( item.createdAt ),
            User: item.userEmail,
            Group: String( item.groupId ),
            Question: item.question,
            Article: item.articleKbId || "—",
            Rating: this.ratingLabel( item ),
            Reason: item.scoreReason || "",
            Comment: item.scoreComment || ""
        };
    }

    private ratingLabel( item: AgentKbExchange ): string {
        if( item.score === 1 )
            return this.$t( "AGENTKB_ratingUp" ) as string;

        if( item.score === -1 )
            return this.$t( "AGENTKB_ratingDown" ) as string;

        return "—";
    }

    private ratingClass( item: AgentKbExchange ): string {
        if( item.score === 1 )
            return "v-agent-kb__rating--up";

        if( item.score === -1 )
            return "v-agent-kb__rating--down";

        return "";
    }

    /* Le transfert : annonce ET parti, annonce sans etre parti, ou rien. */
    private supportLabel( item: AgentKbExchange ): string {
        if( item.supportSentAt !== null )
            return this.$t(
                "AGENTKB_supportSent", [ this.formatDate( item.supportSentAt ) ]
            ) as string;

        if( item.supportReason !== null )
            return this.$t( "AGENTKB_supportFailed" ) as string;

        return "—";
    }

    private ratingFilterLabel( rating: RatingFilter ): string {
        return this.$t( "AGENTKB_filter_" + rating ) as string;
    }

    private ratingFilterCount( rating: RatingFilter ): number {
        return this.totals[ rating ];
    }

    private periodLabel( period: Period ): string {
        return this.$t( "AGENTKB_period" + period ) as string;
    }

    private formatDate( iso: string ): string {
        const date = new Date( iso );

        if( isNaN( date.getTime() ) )
            return "—";

        return date.toLocaleDateString( "fr-FR", {
            day: "numeric", month: "short", hour: "2-digit", minute: "2-digit"
        } );
    }

    /* La circonference d'un anneau de rayon 52, et la part a tracer. */
    private ringDash( percent: number ): string {
        const circumference = 2 * Math.PI * 52;

        return `${ circumference * percent / 100 } ${ circumference }`;
    }

    private destroyChart(): void {
        if( this.chart !== null ) {
            this.chart.destroy();
            this.chart = null;
        }
    }

    /* Les questions par jour, jours sans question compris : le service ne rend que
       les jours qui en ont, une courbe sans les autres mentirait sur le rythme. */
    private buildChart(): void {
        this.destroyChart();

        const canvas = this.$refs.chartCanvas as HTMLCanvasElement | undefined;

        if( canvas === undefined || this.usage === null )
            return;

        const counts: { [ day: string ]: number } = {};

        this.usage.items.forEach( item => {
            if( item.cle !== null )
                counts[ item.cle.slice( 0, 10 ) ] = item.echanges;
        } );

        const labels: string[] = [];
        const data: number[] = [];
        const day = new Date();

        day.setDate( day.getDate() - this.period + 1 );

        for( let index = 0; index < this.period; index++ ) {
            const key = isoDay( day );

            labels.push( day.toLocaleDateString( "fr-FR", { day: "numeric", month: "short" } ) );
            data.push( counts[ key ] || 0 );

            day.setDate( day.getDate() + 1 );
        }

        const accent = this.themeColor( "--accent", "#FF6C00" );
        const grid = this.themeColor( "--border-subtle", "#EDEDED" );
        const muted = this.themeColor( "--fg-muted", "#9A9EA2" );

        this.chart = new Chart( canvas, {
            type: "line",
            data: {
                labels,
                datasets: [ {
                    label: this.$t( "AGENTKB_chartQuestions" ) as string,
                    data,
                    borderColor: accent,
                    backgroundColor: this.hexToRgba( accent, 0.12 ),
                    borderWidth: 2,
                    fill: true,
                    tension: 0.35,
                    pointRadius: 0,
                    pointHoverRadius: 4,
                    pointHoverBackgroundColor: accent
                } ]
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                interaction: { intersect: false, mode: "index" },
                plugins: { legend: { display: false } },
                scales: {
                    x: {
                        grid: { display: false },
                        border: { display: false },
                        ticks: {
                            color: muted, font: { size: 11 }, maxRotation: 0, maxTicksLimit: 8
                        }
                    },
                    y: {
                        beginAtZero: true,
                        grid: { color: grid },
                        border: { display: false },
                        ticks: { color: muted, font: { size: 11 }, precision: 0 }
                    }
                }
            }
        } );
    }

    private themeColor( name: string, fallback: string ): string {
        const value = getComputedStyle( document.documentElement )
            .getPropertyValue( name )
            .trim();

        return value !== "" ? value : fallback;
    }

    private hexToRgba( hex: string, alpha: number ): string {
        const match = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec( hex.trim() );

        if( match === null )
            return hex;

        const r = parseInt( match[ 1 ], 16 );
        const g = parseInt( match[ 2 ], 16 );
        const b = parseInt( match[ 3 ], 16 );

        return `rgba( ${ r }, ${ g }, ${ b }, ${ alpha } )`;
    }
}
